import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { exportPlanningAction, postExportDataAction } from '../../actions/shift';
import ExportButton from '../../components/ExportButton';
import { IShift } from '../../types/shift';
import { ILine, IPole, IPool } from '../../types/structure';
import styles from './export.module.scss';
import { getBrightness, getWeekNumber } from '../../utils';

const Step = ({
  step,
  labels,
  isLast,
  legend,
  shiftWidth,
  activitiesOptions = [],
}: {
  step: IShift,
  labels: any,
  isLast: boolean,
  legend: any,
  shiftWidth: number,
  activitiesOptions: any,
}) => {
  const other = activitiesOptions?.find((ac : any) => ac.value === step.activity);
  const leftIndex = labels.findIndex((l: any) => l.label === step.slices[0]);
  const color = step.structure.poles.find(
    (p: IPole) => p._id === step.pole,
  )?.activities.find((a: any) => a.label === step.activity)?.color
  || step.structure.poles.find((p: IPole) => p._id === step.pole)?.color
  || other?.color || '#FF0000';

  const width = step.slices.length * shiftWidth;

  const activity = other?.label
  || step.activity
  || legend.find((l: any) => l.value === step.activity)?.label;

  return <div className={`${styles.step} ${isLast ? styles.isLast : ''}`}
    style={{
      backgroundColor: color,
      width,
      left: leftIndex * shiftWidth,
    }}
  >
    <p style={{
      backgroundColor: color,
      color: getBrightness(color) ? '#000000' : '#FFFFFF',
    }}>{activity}</p>
  </div>;
};

const ShiftsRow = (
  {
    shifts,
    labels,
    isLast,
    hours = null,
    legend,
    shiftWidth,
    activitiesOptions,
  }
  :{
    shifts: IShift[],
    labels: any,
    isLast: boolean
    hours?: number | null,
    legend: any,
    shiftWidth: number,
    activitiesOptions: any
  },
) => (
    <div className={styles.shifts}>
      <div className={styles.slots}
      >
        {labels?.map((d: any) => {
          const isHour = d.label?.includes(':00');
          const isHalf = (d.label?.includes(':15') || d.label?.includes(':30') || d.label?.includes(':45'));
          return (
            <div
              key={d.label}
              className={`${styles.slot} ${isHour ? styles.hour : ''} ${isHalf ? styles.half : ''}`}
              style={{ width: shiftWidth }}
            >
            </div>
          );
        })}
        {shifts.map((s: IShift) => <Step
          key={s._id}
          step={s}
          labels={labels}
          isLast={isLast}
          legend={legend}
          shiftWidth={shiftWidth}
          activitiesOptions={activitiesOptions}
        />)}
      </div>
      <div className={styles.count}>
        {hours && <p>{hours}</p>}
      </div>
    </div>
);

const toExportRefId = (value: unknown): string | null => {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object' && '_id' in (value as object)) {
    const id = (value as { _id: unknown })._id;
    return id == null ? null : String(id);
  }
  return String(value);
};

const userBelongsToPoles = (user: { poles?: unknown[] }, polesFilter: string[]): boolean => {
  if (polesFilter.length === 0) {
    return true;
  }
  const userPoles = (user.poles || [])
    .map((p) => toExportRefId(p))
    .filter((id): id is string => Boolean(id));
  return polesFilter.some((poleId) => userPoles.includes(poleId));
};

const Export = ({ isPreview = false }:{ isPreview?: boolean }) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [elements, setElements] = useState([]);
  const [usersTotal, setUsersTotal] = useState<any[]>([]);
  const [activitiesOptions, setActivitesOptions] = useState<any[]>([]);
  const [legend, setLegend] = useState([]);
  const [fileName, setFileName] = useState<string>('planning.pdf');
  const { token } = params;
  const structure = searchParams.get('structure');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const poles = searchParams.get('poles');
  const planningView = searchParams.get('planningView');
  const favoriteWeekId = searchParams.get('favoriteWeekId');

  const [shiftWidth, setShiftWidth] = useState(6);
  const downloadFile = async () => {
    const payload: Record<string, unknown> = {
      structure,
      startDate,
      poles: poles?.split(','),
      endDate,
      planningView,
    };
    if (favoriteWeekId) {
      payload.favoriteWeekId = favoriteWeekId;
    }
    const res = await exportPlanningAction(dispatch, payload, fileName);
    return res;
  };

  const getExportData = async () => {
    if (token && structure && (startDate || favoriteWeekId)) {
      const payload: Record<string, unknown> = {
        structure,
        startDate,
        poles: poles?.split(','),
        endDate,
      };
      if (favoriteWeekId) {
        payload.favoriteWeekId = favoriteWeekId;
      }
      const res = await postExportDataAction(dispatch, token, payload);
      if (res.data && startDate) {
        const structureName = res.data?.structure?.name;
        const weekNumber = endDate && getWeekNumber(new Date(endDate));
        const legendData : any = [];
        const activities = res?.data?.activitiesOptions || [];
        setActivitesOptions(activities);

        const pools = res.data?.pools;
        const polesFilter = poles?.split(',').map((item) => item.trim()).filter(Boolean) || [];
        const users = (res.data?.users || []).filter(
          (u: { poles?: unknown[] }) => userBelongsToPoles(u, polesFilter),
        );

        // Liste des emails prioritaires à mettre en premier
        const priorityEmails = [
          'valentinar7000@gmail.com',
          'pierre399949@gmail.com',
          'arnaudcatalan@hotmail.com',
          'theo.herb@icloud.com',
          'samsamgirard89@gmail.com',
          'tristan.bucko@laposte.net',
          'kmljhu@sfr.fr',
          'larry.aurore@gmail.com',
          'sdcfgvh@gmail.com',
          'alexandreleconte10@gmail.com',
          'gabriel.susset@hotmail.fr',
          'sabrinadelage@yahoo.fr',
          'pierre.perello@gmail.com',
          'justinjouy@gmail.com',
          'mauguetheo@gmail.com',
          'kmjh@sfr.fr',
          'jsnmn@sfr.fr',
        ];

        // Trier les users en mettant ceux avec les emails prioritaires en premier
        const sortedUsers = users ? [...users].sort((a: any, b: any) => {
          const emailA = a?.profile?.email || a?.email || '';
          const emailB = b?.profile?.email || b?.email || '';
          const indexA = priorityEmails.indexOf(emailA);
          const indexB = priorityEmails.indexOf(emailB);

          // Si les deux sont dans la liste prioritaire, garder l'ordre de la liste
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          // Si seulement A est prioritaire, le mettre en premier
          if (indexA !== -1) return -1;
          // Si seulement B est prioritaire, le mettre en premier
          if (indexB !== -1) return 1;
          // Sinon, garder l'ordre original
          return 0;
        }) : [];

        const rows : any = [];
        const templateName = res.data?.favoriteWeek?.name;
        const isTemplate = Boolean(res.data?.favoriteWeek);
        const header = {
          type: 'header',
          label: isTemplate
            ? `PLANNING ${structureName} - Modèle ${templateName}`
            : `PLANNING ${structureName} - ${endDate ? `Semaine ${weekNumber} -` : ''}`,
        };

        const dateSpace = {
          type: 'space',
        };

        const dates : any = [];
        const shifts = res.data?.shifts.filter((s:IShift) => {
          const shiftUserId = toExportRefId(s.user);
          if (planningView === 'global') {
            if (s.line) return s;
            if (shiftUserId && !sortedUsers.find((u:any) => toExportRefId(u._id) === shiftUserId)) {
              return null;
            }
            return s;
          }
          if (planningView === 'users') {
            if (s.line) return null;
            if (shiftUserId && !sortedUsers.find((u:any) => toExportRefId(u._id) === shiftUserId)) {
              return null;
            }
            return s;
          }

          if (planningView === 'pools') {
            return s.line ? s : null;
          }
          return s;
        });
        const usersTable : any[] = [];
        // Create legend

        shifts.forEach((shift: IShift) => {
          if (!legendData.find((l:any) => l.value === shift.activity)) {
            const other = activitiesOptions?.find((ac : any) => ac.value === shift.activity
            || ac.label === shift.activity);
            const color = res.data?.structure.poles.find(
              (p: IPole) => p._id === shift.pole,
            )?.activities.find((a: any) => a.label === shift.activity)?.color
            || shift.structure.poles.find((p: IPole) => p._id === shift.pole)?.color
            || other?.color
            || res.data.legend.find((l: any) => l.value === shift.activity)?.color
            || '#FF0000';

            const label = other?.label
            || res.data.legend.find((l: any) => l.value === shift.activity)?.label
            || shift.activity;

            legendData.push({
              value: shift.activity,
              color,
              label,
            });
          }
        });

        // Use structure schedules hours from API (not cropped to visible shifts)
        const labels = res.data?.hours || [];

        // clac slot size
        setShiftWidth(1500 / labels.length);

        if (endDate) {
          let current = 0;
          const end = 6;
          while (current <= end) {
            const newDate = new Date(new Date(startDate)
              .setDate(new Date(startDate).getDate() + current));
            dates.push({
              date: newDate,
              dateLabel: isTemplate
                ? new Intl.DateTimeFormat('fr', { weekday: 'long' }).format(newDate)
                : new Intl.DateTimeFormat('fr', {
                  weekday: 'short',
                  month: 'long',
                  day: 'numeric',
                }).format(newDate),
              labels,
              shifts: shifts?.filter(
                (s: IShift) => new Date(s.date).toISOString() === newDate.toISOString(),
              ),
            });
            if (!endDate) {
              current = 6;
            } else {
              current += 1;
            }
          }
        } else {
          const newDate = new Date(startDate);
          dates.push({
            date: newDate,
            dateLabel: isTemplate
              ? new Intl.DateTimeFormat('fr', { weekday: 'long' }).format(newDate)
              : new Intl.DateTimeFormat('fr', {
                weekday: 'short',
                month: 'long',
                day: 'numeric',
              }).format(newDate),
            labels,
            shifts: shifts?.filter(
              (s: IShift) => new Date(s.date).toISOString() === newDate.toISOString(),
            ),
          });
        }
        let name = 'planning';
        if (isTemplate) {
          name = `modele-${templateName || 'semaine'}.pdf`;
        } else if (dates.length > 1) {
          const endDay = new Intl.DateTimeFormat('fr', {
            weekday: 'short',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }).format(dates[dates.length - 1].date);
          header.label += `${dates[0].dateLabel} au ${endDay}`;
          name += `-${dates[0].dateLabel}-au-${endDay}.pdf`;
        } else {
          const day = new Intl.DateTimeFormat('fr', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }).format(new Date(startDate));
          header.label += `${day}`;
          name += `-${day}.pdf`;
        }
        setFileName(name.replaceAll(' ', '-'));
        rows.push(header);
        dates.forEach((day: any, i: number) => {
          const dayChildren: any[] = [];
          if (i > 0) {
            dayChildren.push(dateSpace);
          }
          dayChildren.push({
            type: 'label',
            day: day.dateLabel,
            labels,
          });
          pools.forEach((p: IPool) => {
            const pool : any = {
              type: 'pool',
              pool: p.name,
              lines: [],
            };
            p.lines.forEach((l: ILine) => {
              pool.lines.push({
                label: l.name,
                key: `${l.name}-${day.dateLabel}`,
                shifts: day?.shifts?.filter(
                  (s:IShift) => toExportRefId(s.line) === toExportRefId(l._id),
                ),
                labels,
              });
            });

            dayChildren.push(pool);
          });
          if (planningView === 'pools') {
            dayChildren.push({ type: 'pool-end' });
          }
          sortedUsers.forEach((u: any, userIndex: number) => {
            const absencesOptions = u.contract?.isAnnualized ? ['paid vacation'] : [];
            const userName = `${u?.profile?.firstName} ${u?.profile?.lastName}`;
            const user : any = {
              type: 'user',
              user: userName,
              color: u.color,
              shifts: day?.shifts?.filter(
                (s:IShift) => toExportRefId(s.user) === toExportRefId(u._id),
              ),
              labels,
              isLast: userIndex === sortedUsers.length - 1,
            };
            const slices = day?.shifts?.filter(
              (s:IShift) => toExportRefId(s.user) === toExportRefId(u._id)
              && !absencesOptions.find((a: string) => a === s.activity),
            ).map((s: IShift) => s.slices);
            const hours = slices.flat().length / 12;
            const userTableIndex : number = usersTable.findIndex((ut) => ut._id === u._id);
            if (hours > 0) {
              const minutes = (hours - Math.floor(hours)) * 60;
              user.hours = `${Math.floor(hours)}H${minutes > 0 ? minutes.toFixed() : ''}`;
              if (userTableIndex !== -1) {
                usersTable[userTableIndex].total += hours;
              }
              if (userTableIndex === -1) {
                usersTable.push({
                  _id: u._id,
                  name: userName,
                  color: u.color,
                  total: hours,
                });
              }
            }
            dayChildren.push(user);
          });
          rows.push({ type: 'day', children: dayChildren });
        });

        const usersTableFormated : any[] = usersTable.map((u) => {
          const hours = u?.total;
          const minutes = (hours - Math.floor(hours)) * 60;
          return {
            ...u,
            hours: `${Math.floor(hours)}H${minutes > 0 ? minutes.toFixed() : ''}`,
          };
        });
        console.log(planningView);
        setUsersTotal(usersTableFormated);
        setElements(rows);
        setLegend(legendData);
      }
    }
  };

  useEffect(() => {
    if (token && structure && (startDate || favoriteWeekId)) {
      getExportData();
    }
  }, []);
  const renderElementContent = (element: any) => (
    <>
          {element.type === 'header'
            && <div className={styles.header}>
              <h2>{element.label}</h2>
            </div>
          }
          {element.type === 'label'
            && <div className={styles.label}>
              <div className={styles.day}>
                {element.day}
              </div>
              <div className={styles.hours}>
                {element?.labels?.filter((l: any) => l.label !== '02:00').map((d: any) => {
                  const splited = d.label.split(':');
                  let hour = d.label?.includes(':00') && splited[0];
                  if (hour && hour.charAt(0) === '0') {
                    hour = hour.slice(1);
                  }
                  if (hour) {
                    hour += 'H';
                  }
                  let minutes = d.label?.includes(':30') && d.label;
                  if (minutes && minutes.charAt(0) === '0') {
                    minutes = minutes.slice(1);
                  }
                  const isActive = (d.label?.includes(':00') || d.label?.includes(':15') || d.label?.includes(':30') || d.label?.includes(':45'));
                  return (
                    <div
                      key={d.label}
                      className={`${styles.slot} ${isActive ? styles.active : ''}`}
                      style={{ width: shiftWidth }}
                    >
                      {hour && <p className={styles.hour}>{hour}</p>}
                      {minutes && <p className={styles.minutes}>{minutes}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          }
          {element.type === 'pool' && planningView !== 'users'
            && <div className={styles['pool-row']}>
              <div className={styles.pool}>
                <p>{element.pool}</p>
              </div>
              <div className={styles.lines}>
                {element.lines.map((l: any, lineIndex: number) => <div
                  key={l.key}
                  className={styles.line}
                >
                  <div className={styles['line-label']}>
                    <p>{l.label}</p>
                  </div>
                  <div className={styles.shifts}>
                    <ShiftsRow
                      shifts={l.shifts}
                      labels={l.labels}
                      isLast={lineIndex === element.lines.length - 1 }
                      legend={legend}
                      shiftWidth={shiftWidth}
                      activitiesOptions={activitiesOptions}
                    />
                  </div>
                </div>)}
              </div>
            </div>
          }
          {element.type === 'user' && planningView !== 'pools'
            && <div className={`${styles['user-row']} ${element.isLast ? styles.isLast : ''}`}>
                <div className={styles.user}
                    style={{ backgroundColor: element?.color !== '#FFFFFF' ? element?.color : 'transparent' }}
                  >
                  <p style={{ color: element?.color && element?.color !== '#FFFFFF' && element?.color !== 'transparent' && !getBrightness(element?.color) ? '#FFFFFF' : '#000000' }}>{element.user}</p>
                </div>
                <div className={styles.shifts}>
                  <ShiftsRow
                    shifts={element.shifts}
                    labels={element.labels}
                    activitiesOptions={activitiesOptions}
                    isLast={true}
                    hours={element?.hours}
                    legend={legend}
                    shiftWidth={shiftWidth}
                  />
                </div>
            </div>
          }
          {element.type === 'break'
            && <div className={styles.break}/>
          }
           {element.type === 'pool-end'
            && <div className={styles['pool-end']}/>
          }
          {element.type === 'space'
            && <div className={styles.space}/>
          }
    </>
  );

  return (
    <div className={`${styles.container} ${isPreview ? styles.preview : ''}`}>
      <div id="export" className={styles.export}>
        {elements.map((element : any, i: number) => {
          if (element.type === 'day') {
            return (
              <div key={`day-${i}`} className={styles.dayBlock}>
                {element.children.map((child: any, j: number) => (
                  <div key={`day-${i}-${j}`} className={styles.element}>
                    {renderElementContent(child)}
                  </div>
                ))}
              </div>
            );
          }
          return (
            <div key={`element-${i}`} className={styles.element}>
              {renderElementContent(element)}
            </div>
          );
        })}
        {usersTotal?.length > 0 && planningView !== 'pools'
          && <div className={styles['users-table']}>
            <div className={styles.users}>
              {usersTotal.map((u: any) => <div
              key={u._id}
              className={styles.user}
              style={{ backgroundColor: u?.color || 'transparent' }}
            >
                <p style={{ color: u?.color && u?.color !== 'transparent' && !getBrightness(u?.color) ? '#FFFFFF' : '#000000' }}>{u.name}</p>
                <p style={{ color: u?.color && u?.color !== 'transparent' && !getBrightness(u?.color) ? '#FFFFFF' : '#000000' }}>{u.hours}</p>
              </div>)}
            </div>
            <div className={styles.legend}>
              <span>Légende</span>
              <div className={styles.items}>
                {legend.filter(
                  (a: any) => a.value !== 'rest'
                  && a.value !== 'sick'
                  && a.value !== 'Leave without pay'
                  && a.value !== 'conventional leave'
                  && a.value !== 'other'
                  && a.value !== 'dress up day'
                  && a.value !== 'unjustified absence',
                ).map((l : any) => <div
                  key={l.value}
                  className={styles.item}
                >
                <div
                  className={styles.color}
                  style={{
                    backgroundColor: l.color,
                  }}
                >
                </div>
                <p>{l.label}</p>
                </div>)}
              </div>
              </div>
          </div>
        }
        {usersTotal?.length === 0
          && <div className={`${styles['users-table']} ${styles.pools}`}>
            <div className={styles.legend}>
              <span>Légende</span>
              <div className={styles.items}>
                {legend.filter(
                  (a: any) => a.value !== 'rest'
                  && a.value !== 'sick'
                  && a.value !== 'Leave without pay'
                  && a.value !== 'conventional leave'
                  && a.value !== 'other'
                  && a.value !== 'dress up day'
                  && a.value !== 'unjustified absence',
                ).map((l : any) => <div
                  key={l.value}
                  className={styles.item}
                >
                <div
                  className={styles.color}
                  style={{
                    backgroundColor: l.color,
                  }}
                >
                </div>
                <p>{l.label}</p>
                </div>)}
              </div>
              </div>
          </div>
        }
      </div>
        {isPreview
          && <div className={styles.download}>
            <ExportButton
              callBack={downloadFile}
              isPreview
            />
          </div>
        }
    </div>
  );
};

export default Export;
